import * as React from 'react';
import { useNavigate} from 'react-router-dom';
import { useAlarms} from '@tyndo/hooks';
import { Button} from '@tyndo/ui-kit';
import { AlertCircle, AlertTriangle, Info, Bell, ChevronRight} from 'lucide-react';

/**
 * Tyndo Severity Stripe — global plant-health ribbon under the TopBar.
 *
 * Reads from `useAlarms()` (live SignalR). When healthy: a single Phosphor
 * dot — quietly affirming the system is alive. When alarms exist, expands
 * into a chip strip showing counts by ISA-18.2 severity. Click → /tyndo.alarms.
 *
 * Visual goal: glanceable from any screen. Feel"alive" but not anxious.
 */
export default function SeverityStripe() {
 const navigate = useNavigate();
 const liveAlarms = useAlarms();
 // Stable empty-array fallback: `useAlarms() ?? []` inline would mint a new
 // array identity every render and defeat the counts memo below.
 const alarms = React.useMemo(() => liveAlarms ?? [], [liveAlarms]);

 const counts = React.useMemo(() => {
 const c = { critical: 0, major: 0, minor: 0, advisory: 0};
 alarms.forEach((a) => {
 // The live SignalR payload may carry severity/priority under varying keys
 // depending on the source hub, so probe both defensively.
 const probe = a as unknown as Record<string, unknown>;
 const sev = String(probe.severity ?? probe.priority ?? probe.type ?? '').toLowerCase();
 if (sev.includes('crit')) c.critical += 1;
 else if (sev.includes('maj') || sev.includes('high')) c.major += 1;
 else if (sev.includes('min') || sev.includes('low')) c.minor += 1;
 else c.advisory += 1;
});
 return c;
}, [alarms]);

 const total = counts.critical + counts.major + counts.minor + counts.advisory;
 const healthy = total === 0;

 return (
 <Button
 type="button"
 variant="ghost"
 onClick={() => navigate('/tyndo.alarms')}
 className="h-auto whitespace-normal w-full justify-start text-left font-normal flex-col items-stretch rounded-none [&_svg]:size-3.5 hover:text-foreground group relative w-full overflow-hidden border-b border-border bg-surface-raised px-4 py-1.5 text-left transition-colors hover:bg-accent/40"
 aria-label={
 healthy
 ? 'Plant healthy — no active alarms'
 : `${total} active alarms, click to open Alarms`
}
 >
 {/* Top accent — Phosphor when healthy, severity-coloured when not */}
 <span
 aria-hidden
 className="absolute inset-x-0 top-0 h-[2px] origin-left"
 style={{
 background: healthy
 ? 'linear-gradient(90deg, hsl(var(--data-phosphor)/0) 0%, hsl(var(--data-phosphor)/0.6) 50%, hsl(var(--data-phosphor)/0) 100%)'
 : counts.critical > 0
 ? 'hsl(var(--alarm-critical))'
 : counts.major > 0
 ? 'hsl(var(--alarm-major))'
 : 'hsl(var(--alarm-minor))',
}}
 />

 <div className="flex items-center gap-3">
 {healthy ? (
 <>
 <span
 className="relative flex h-2 w-2 items-center justify-center"
 aria-hidden
 >
 <span className="absolute inline-flex h-full w-full rounded-full bg-data-phosphor opacity-60 tyndo-pulse" />
 <span className="relative inline-flex h-2 w-2 rounded-full bg-data-phosphor" />
 </span>
 <span className="text-xs text-muted-foreground">
 <span className="text-foreground font-medium">All systems live</span>
 <span className="mx-2 text-muted-foreground/50">·</span>
 No active alarms
 </span>
 </>
 ) : (
 <>
 <Bell className="h-3.5 w-3.5 text-alarm-major" />
 <span className="text-xs text-foreground">
 <span className="font-semibold">{total}</span>{''}
 active alarm{total === 1 ? '' : 's'}
 </span>
 <div className="flex items-center gap-1.5">
 {counts.critical > 0 && (
 <SeverityChip
 Icon={AlertCircle}
 label="Critical"
 count={counts.critical}
 color="alarm-critical"
 />
 )}
 {counts.major > 0 && (
 <SeverityChip
 Icon={AlertTriangle}
 label="Major"
 count={counts.major}
 color="alarm-major"
 />
 )}
 {counts.minor > 0 && (
 <SeverityChip
 Icon={AlertTriangle}
 label="Minor"
 count={counts.minor}
 color="alarm-minor"
 />
 )}
 {counts.advisory > 0 && (
 <SeverityChip
 Icon={Info}
 label="Advisory"
 count={counts.advisory}
 color="alarm-advisory"
 />
 )}
 </div>
 </>
 )}
 <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-muted-foreground/70 t-mono opacity-0 transition-opacity group-hover:opacity-100">
 open alarms
 <ChevronRight className="h-3 w-3" />
 </span>
 </div>
 </Button>
 );
}

function SeverityChip({
 Icon,
 label,
 count,
 color,
}: {
 Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
 label: string;
 count: number;
 color:'alarm-critical' |'alarm-major' |'alarm-minor' |'alarm-advisory';
}) {
 return (
 <span
 className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
 style={{
 backgroundColor: `hsl(var(--${color}) / 0.12)`,
 color: `hsl(var(--${color}))`,
 boxShadow: `inset 0 0 0 1px hsl(var(--${color}) / 0.3)`,
}}
 >
 <Icon className="h-3 w-3" />
 <span>{label}</span>
 <span className="t-tabular tabular-nums font-semibold">{count}</span>
 </span>
 );
}
